// 薪資明細匯出：把 GET /v1/accounting/payslips/export 的結果排成「心理師所得匯出」範本的 xlsx

const HEADERS = ['姓名', '月份', '自費諮商', '專案收入', '活動收入', '薪資單加項項目', '金額', '薪資單減項項目', '金額', '薪資確認狀態'];
// 欄寬沿用範本，範本沒設的欄位依內容抓
const COLUMN_WIDTHS = [9, 8, 10.27, 10.27, 15.73, 17.73, 10, 17.73, 10, 18];
// 加減項（F–I 欄）同一格可能有多行
const MULTI_LINE_COLUMNS = [6, 7, 8, 9];

const CONFIRM_STATUS_TEXT = {
  not_notified: '尚未通知',
  not_confirmed: '已通知（尚未確認）',
  confirmed: '已確認',
  wrong_amount: '金額錯誤',
};

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** 民國年＋兩位數月份，例：2025 年 3 月 → 11403 */
export function toRocMonth(year, month) {
  return (year - 1911) * 100 + month;
}

// 浮點累加可能出現 1234.5000001，取到小數兩位
function toAmount(value) {
  return Math.round((value || 0) * 100) / 100;
}

// 0 留空，同範本
function amountCell(value) {
  const amount = toAmount(value);
  return amount === 0 ? null : amount;
}

// 只有一筆時金額保留數字（Excel 可加總）；多筆時同一格一筆一行
function itemCells(items) {
  if (items.length === 0) {
    return [null, null];
  }
  if (items.length === 1) {
    return [items[0].name, items[0].amount];
  }
  return [
    items.map((item) => item.name).join('\n'),
    items.map((item) => `${item.amount}`).join('\n'),
  ];
}

function isEmptyPayslip(payslip) {
  return toAmount(payslip.self_paid_appointment_amount) === 0
    && toAmount(payslip.project_paid_appointment_amount) === 0
    && toAmount(payslip.activity_amount) === 0
    && payslip.extra_items.length === 0;
}

/** 每張薪資單一列，每列都填姓名；三項金額皆 0 且沒有加減項的月份略過 */
export function payslipsToRows(payslips) {
  return payslips.filter((p) => !isEmptyPayslip(p)).map((p) => {
    const additions = p.extra_items
      .filter((item) => item.amount > 0)
      .map((item) => ({ name: item.name, amount: toAmount(item.amount) }));
    const deductions = p.extra_items
      .filter((item) => item.amount < 0)
      .map((item) => ({ name: item.name, amount: Math.abs(toAmount(item.amount)) }));
    return [
      `${p.professional.family_name}${p.professional.given_name}`,
      toRocMonth(p.year, p.month),
      amountCell(p.self_paid_appointment_amount),
      amountCell(p.project_paid_appointment_amount),
      amountCell(p.activity_amount),
      ...itemCells(additions),
      ...itemCells(deductions),
      CONFIRM_STATUS_TEXT[p.confirm_status] || p.confirm_status,
    ];
  });
}

export async function buildPayslipXlsx(rows) {
  // exceljs 體積大，只在匯出時才載入
  const mod = await import(/* webpackChunkName: "exceljs" */ 'exceljs');
  const ExcelJS = mod.default || mod;
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('薪資明細');
  sheet.columns = COLUMN_WIDTHS.map((width) => ({ width }));
  sheet.addRow(HEADERS);
  rows.forEach((values) => {
    const row = sheet.addRow(values);
    row.alignment = { vertical: 'top' };
    MULTI_LINE_COLUMNS.forEach((col) => {
      row.getCell(col).alignment = { vertical: 'top', wrapText: true };
    });
  });
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { type: XLSX_MIME });
}
